import { Link } from "react-router-dom";
import { useState } from "react";

export default function Pricing() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "Do I need a credit card?",
      answer: "No credit card is required to start using the Free Beta plan."
    },
    {
      question: "Why is AI generation limited?",
      answer: "AI generation has real usage costs. Limits help keep ExamLock sustainable and affordable."
    },
    {
      question: "Can schools pay by invoice?",
      answer: "Yes. School Plan customers can arrange invoice-based billing. Contact us for details."
    },
    {
      question: "Can I change plans later?",
      answer: "Yes. You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing period."
    },
    {
      question: "Do you offer school pilots?",
      answer: "Yes. We can support a small pilot with one teacher, one department, or one year group before wider rollout. Contact us to discuss."
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-sky-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Simple pricing for teachers and schools
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Start with the Free Beta. Upgrade when your class, department, or school needs more.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">

            {/* Free Beta */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-400 transition-all">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">Free Beta</h3>
              <div className="text-5xl font-bold mb-4 text-gray-900">$0</div>
              <p className="text-gray-600 mb-6 h-12">
                Best for teachers trying ExamLock with a small class or pilot group.
              </p>
              <ul className="space-y-3 mb-8 text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Up to 10 students</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>50 AI questions (trial total)</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Secure Test Mode</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Basic monitoring</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Activity logs</li>
              </ul>
              <Link
                to="/teacher-register"
                className="block w-full py-4 bg-blue-600 hover:bg-blue-700 text-white text-center rounded-lg font-semibold text-lg transition-all"
              >
                Start Free
              </Link>
            </div>

            {/* Professional */}
            <div className="bg-blue-600 text-white border-2 border-blue-600 rounded-xl p-8 transform scale-105 shadow-2xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wide">
                  Most Popular
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Professional</h3>
              <div className="text-5xl font-bold mb-4">$9.99<span className="text-xl font-normal">/mo</span></div>
              <p className="text-blue-100 mb-6 h-12">
                For individual teachers using ExamLock regularly.
              </p>
              <ul className="space-y-3 mb-8 text-blue-50">
                <li className="flex items-start"><span className="text-white mr-2 text-lg">✓</span>Up to 50 students</li>
                <li className="flex items-start"><span className="text-white mr-2 text-lg">✓</span>2,000 AI questions/month</li>
                <li className="flex items-start"><span className="text-white mr-2 text-lg">✓</span>Secure assessments</li>
                <li className="flex items-start"><span className="text-white mr-2 text-lg">✓</span>Live monitoring</li>
                <li className="flex items-start"><span className="text-white mr-2 text-lg">✓</span>Email support</li>
              </ul>
              <Link
                to="/teacher-register"
                className="block w-full py-4 bg-white hover:bg-gray-100 text-blue-600 text-center rounded-lg font-semibold text-lg transition-all"
              >
                Start Free
              </Link>
            </div>

            {/* School Plan */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-400 transition-all">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">School Plan</h3>
              <div className="text-5xl font-bold mb-4 text-gray-900">Custom</div>
              <p className="text-gray-600 mb-6 h-12">
                For departments and schools.
              </p>
              <ul className="space-y-3 mb-8 text-gray-700">
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Multiple teacher accounts</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Larger student limits</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>Onboarding support</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>DPA available on request</li>
                <li className="flex items-start"><span className="text-green-600 mr-2 text-lg">✓</span>School setup support</li>
              </ul>
              <Link
                to="/contact"
                className="block w-full py-4 bg-gray-800 hover:bg-gray-900 text-white text-center rounded-lg font-semibold text-lg transition-all"
              >
                Contact Us
              </Link>
            </div>

          </div>

          {/* Pricing Note */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              📌 Pricing may be adjusted as ExamLock develops. Early users and pilot schools will receive clear notice before any changes.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Pricing FAQ</h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full text-left p-6 flex justify-between items-center hover:bg-gray-100 transition-colors rounded-lg"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-gray-400">{openFAQ === index ? "−" : "+"}</span>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Start with the Free Beta and upgrade when you're ready.
          </p>
          <Link
            to="/teacher-register"
            className="inline-block px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 rounded-lg font-semibold text-lg shadow-lg transition-all"
          >
            Start Free
          </Link>
        </div>
      </div>

    </div>
  );
}
